import { Link } from "react-router-dom";
import { BookOpen, ClipboardList, ChevronRight, BarChart3, ShieldCheck } from "lucide-react";
import { useModGetAssignmentsQuery } from "../../api/moderatorApi";

export default function ModeratorDashboardPage() {
  const { data: assignments = [] } = useModGetAssignmentsQuery();

  const pending   = (assignments as any[]).filter((a: any) => a.status === "under_moderation").length;
  const moderated = (assignments as any[]).filter((a: any) => a.status === "moderated").length;

  const stats = [
    { label: "Assigned Courses", val: assignments.length, icon: BookOpen,     color: "indigo"  },
    { label: "Awaiting Review",  val: pending,            icon: ClipboardList, color: "amber"   },
    { label: "Feedback Sent",    val: moderated,          icon: BarChart3,     color: "violet"  },
  ];

  const nav = [
    {
      title: "My Assigned Courses",
      desc:  `${assignments.length} course${assignments.length !== 1 ? "s" : ""} to moderate`,
      icon:  <ClipboardList className="w-6 h-6 text-amber-500" />,
      href:  "/exams/moderator/courses",
      color: "amber",
      badge: pending,
    },
  ];

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {stats.map((s, i) => (
          <div key={i} className="bg-white border border-gray-100 p-5 rounded-3xl shadow-sm flex items-center gap-4">
            <div className={`w-11 h-11 rounded-2xl flex items-center justify-center bg-${s.color}-50 text-${s.color}-600 shrink-0`}>
              <s.icon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-0.5">{s.label}</div>
              <div className="text-xl font-black text-gray-900">{s.val}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
        <div className="flex items-center gap-5">
          <div className="w-14 h-14 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl shadow-gray-200">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-gray-900 tracking-tight leading-none uppercase">Moderator</h1>
            <p className="text-gray-400 text-sm mt-1.5 font-medium">Review assigned courses and submit feedback to the Chief Moderator.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {nav.map((c) => (
          <Link key={c.href} to={c.href}
            className="relative bg-white border border-gray-100 rounded-3xl p-6 shadow-sm hover:shadow-md hover:border-gray-200 transition-all group flex flex-col gap-4">
            {(c.badge ?? 0) > 0 && (
              <span className="absolute top-4 right-4 min-w-[1.25rem] h-5 px-1.5 bg-rose-500 text-white text-[9px] font-black rounded-full flex items-center justify-center">
                {c.badge}
              </span>
            )}
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-${c.color}-50`}>
              {c.icon}
            </div>
            <div>
              <p className="font-black text-gray-900 uppercase tracking-tight group-hover:text-indigo-600 transition-colors">{c.title}</p>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-1">{c.desc}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-gray-200 group-hover:text-indigo-400 transition-colors self-end mt-auto" />
          </Link>
        ))}
      </div>

      <div className="bg-gray-900 text-white rounded-[2.5rem] p-10 flex items-center gap-8 shadow-2xl shadow-gray-200">
        <div className="w-16 h-16 bg-violet-500/10 rounded-3xl flex items-center justify-center shrink-0 border border-violet-500/20">
          <ShieldCheck className="w-8 h-8 text-violet-400" />
        </div>
        <div>
          <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-violet-400 mb-2">Your Role</h4>
          <p className="text-sm text-gray-400 leading-relaxed font-medium max-w-3xl">
            Review examiner-submitted scores for your assigned courses. Your feedback is forwarded to the
            Chief Moderator, who consolidates it and passes it to the Chief Examiner for corrective action.
          </p>
        </div>
      </div>
    </div>
  );
}
